import { useState, useEffect, useCallback } from "react";
import { usePools } from "../hooks/usePools";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/permissions";
import { SplitWheel } from "../components/SplitWheel";
import { PoolOverview } from "../components/PoolOverview";
import { Recipients } from "../components/Recipients";
import { ProfitSimulator } from "../components/ProfitSimulator";
import { PayoutHistory } from "../components/PayoutHistory";
import { ClientPayments } from "../components/ClientPayments";
import { SecurityBadge } from "../components/SecurityBadge";
import { PoolSwitcher } from "../components/PoolSwitcher";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { EditPercentagesDialog } from "../components/EditPercentagesDialog";
import { BankAccountDialog } from "../components/BankAccountDialog";
import { InviteRecipientDialog } from "../components/InviteRecipientDialog";
import { ExecutePayoutDialog } from "../components/ExecutePayoutDialog";
import { StripeInvoiceDialog } from "../components/StripeInvoiceDialog";
import { useConnectStatus } from "../components/StripeConnectSettings";
import { CreatePoolDialog } from "../components/CreatePoolDialog";
import { PoolDeleteConfirmModal } from "../components/PoolDeleteConfirmModal";
import { OrgAlertsBanner } from "../components/OrgAlertsBanner";
import { BetaModeNotice } from "../components/BetaBadge";
import { EmptyStateCard, PageShell } from "../components/EmptyStateWrapper";
import { LoadingIndicator } from "../components/ShellContentFrame";
import { Sliders, UserPlus, Send, Trash2 } from "lucide-react";
import { wheelMembers } from "../lib/poolMembers";
import { api, extractAxiosError } from "../lib/api";
import { useNavigate, useSearchParams, Link, useLocation } from "react-router-dom";
import { CreateOrganisationForm } from "../components/CreateOrganisationForm";
import { hasLeftWorkspaceFlag, clearLeftWorkspaceFlag } from "../lib/clientStorage";
import { toast } from "sonner";

export function DashboardPage() {
    const { activeOrg, memberships, orgReady, refreshMe, user } = useAuth();
    const { pools, activePool, activePoolId, selectPool, loading, refetch, updateActivePool, removePool } = usePools();
    const [payouts, setPayouts] = useState([]);
    const [clientPayments, setClientPayments] = useState([]);
    const [highlightId, setHighlightId] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [bankFor, setBankFor] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [executeOpen, setExecuteOpen] = useState(false);
    const [stripeOpen, setStripeOpen] = useState(false);
    const [createPoolOpen, setCreatePoolOpen] = useState(false);
    const [deletePoolOpen, setDeletePoolOpen] = useState(false);
    const [deletePoolBusy, setDeletePoolBusy] = useState(false);
    const [pendingInviteCount, setPendingInviteCount] = useState(0);
    const role = activeOrg?.role;
    const navigate = useNavigate();
    const location = useLocation();
    const leftWorkspace = location.state?.leftWorkspace || hasLeftWorkspaceFlag();
    const [params, setParams] = useSearchParams();
    const { ready: connectReady } = useConnectStatus(activeOrg?.id);

    const loadPayouts = useCallback(async () => {
        if (!activePool?.id) { setPayouts([]); return; }
        const { data } = await api.get(`/pools/${activePool.id}/payouts`);
        setPayouts(data.payouts || []);
    }, [activePool?.id]);

    const syncClientPayments = useCallback(async (showToast = false) => {
        if (!activePool?.id) {
            setClientPayments([]);
            return;
        }
        if (activeOrg?.id && can.createInvoice(role)) {
            try {
                const { data } = await api.post(`/payments/reconcile?org_id=${activeOrg.id}`);
                if (data.credited_count > 0) {
                    if (showToast) {
                        toast.success(`Synced ${data.credited_count} client payment${data.credited_count > 1 ? "s" : ""} to pool revenue`);
                    }
                    refetch();
                }
            } catch (_e) {
                /* reconcile optional */
            }
        }
        try {
            const { data } = await api.get(`/pools/${activePool.id}/client-payments`);
            setClientPayments(data.payments || []);
        } catch (_e) {
            setClientPayments([]);
        }
    }, [activePool?.id, activeOrg?.id, role, refetch]);

    useEffect(() => { loadPayouts(); }, [loadPayouts]);
    useEffect(() => { syncClientPayments(false); }, [syncClientPayments]);

    useEffect(() => {
        if (!orgReady || !user || (memberships || []).length > 0) {
            setPendingInviteCount(0);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/invitations/inbox/count");
                if (!cancelled) setPendingInviteCount(data.count || 0);
            } catch (_e) {
                if (!cancelled) setPendingInviteCount(0);
            }
        })();
        return () => { cancelled = true; };
    }, [orgReady, user, memberships]);

    useEffect(() => {
        const sid = params.get("stripe_session_id");
        const cancelled = params.get("stripe_cancelled");
        if (cancelled) {
            toast.info("Stripe checkout cancelled");
            params.delete("stripe_cancelled");
            setParams(params, { replace: true });
            return;
        }
        if (sid) {
            let attempts = 0;
            const tick = async () => {
                if (attempts >= 5) return;
                attempts++;
                try {
                    const { data } = await api.get(`/payments/status/${sid}`);
                    if (data.payment_status === "paid") {
                        const cur = data.currency || "GBP";
                        const gross = (data.amount_total || 0) / 100;
                        const net = data.net_amount ?? gross;
                        const fmt = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: cur }).format(n);
                        const desc = data.revenue_credited
                            ? `${fmt(net)} added to pool revenue (${fmt(gross)} received)`
                            : `${fmt(gross)} received — refresh the dashboard if revenue hasn't updated yet`;
                        toast.success("Payment received", { description: desc });
                        params.delete("stripe_session_id");
                        setParams(params, { replace: true });
                        refetch();
                        syncClientPayments(true);
                        return;
                    }
                    if (data.status === "expired") {
                        toast.error("Payment session expired");
                        params.delete("stripe_session_id");
                        setParams(params, { replace: true });
                        return;
                    }
                    setTimeout(tick, 2000);
                } catch (_e) { /* retry */ }
            };
            tick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChanged = (resp) => {
        if (resp?.status === "applied" || resp?.pool) {
            refetch().then(loadPayouts);
        } else if (resp?.approval_id) {
            navigate("/approvals");
        }
    };

    const poolHasHistory = Boolean(
        activePool?.last_payout
        || (activePool?.total_revenue || 0) > 0
        || payouts.length > 0,
    );

    const handleDeletePool = async () => {
        if (!activePool?.id || deletePoolBusy) return;
        setDeletePoolBusy(true);
        try {
            const { data } = await api.delete(`/pools/${activePool.id}`);
            removePool(activePool.id);
            setDeletePoolOpen(false);
            toast.success(
                data?.status === "archived"
                    ? "Pool archived successfully"
                    : "Pool deleted successfully",
            );
            navigate("/pools");
            refetch();
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not delete pool"));
        } finally {
            setDeletePoolBusy(false);
        }
    };

    const handleOrgCreated = useCallback(async () => {
        clearLeftWorkspaceFlag();
        await refreshMe();
    }, [refreshMe]);

    const hasMemberships = (memberships || []).length > 0;
    const poolsLoading = orgReady && hasMemberships && !!activeOrg?.id && loading && pools.length === 0;
    const showOrgEmpty = orgReady && !hasMemberships;
    const showPoolEmpty = orgReady && hasMemberships && !!activeOrg?.id && !loading && !activePool;
    const showDashboard = orgReady && !!activePool && !loading;

    const showEmptyState = showOrgEmpty || showPoolEmpty;

    return (
        <PageShell className={`flex min-h-0 flex-1 flex-col${showEmptyState ? " dashboard-empty-page" : ""}`}>
            <div className="relative flex min-h-0 flex-1 flex-col w-full">
            {showEmptyState && (
                <div className="dashboard-empty-layout" data-testid="dashboard-empty-layout">
                    <div className="dashboard-empty-center" data-testid="dashboard-empty-viewport">
                        {showOrgEmpty && (
                            <EmptyStateCard inline testId="org-empty">
                                {leftWorkspace ? (
                                    <>
                                        <p className="font-heading text-2xl">You&apos;ve left your workspace</p>
                                        <p className="mt-2 text-white/45">
                                            You&apos;re still signed in. Accept a new invite from Invite Center, or create your own organisation below.
                                        </p>
                                        <Link to="/invites" className="btn-ghost mt-5 inline-flex rounded-md px-4 py-2 text-sm" data-testid="dashboard-check-invites">
                                            Open Invite Center
                                        </Link>
                                        <CreateOrganisationForm onCreated={handleOrgCreated} />
                                    </>
                                ) : pendingInviteCount > 0 ? (
                                    <>
                                        <p className="font-heading text-2xl">You have a pending invitation</p>
                                        <p className="mt-2 text-white/45">
                                            Accept it to join the workspace you were invited to — no need to create a new organisation.
                                        </p>
                                        <Link to="/invites" className="btn-neon mt-5 inline-flex rounded-md px-4 py-2 text-sm" data-testid="dashboard-open-invites">
                                            Open Invite Center
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <p className="font-heading text-2xl">Create your organisation</p>
                                        <p className="mt-2 text-white/45">Set up a workspace to create pools and manage payouts.</p>
                                        <CreateOrganisationForm onCreated={handleOrgCreated} />
                                    </>
                                )}
                            </EmptyStateCard>
                        )}

                        {showPoolEmpty && (
                            <EmptyStateCard inline testId="pools-empty">
                                <p className="font-heading text-2xl">No pools yet</p>
                                <p className="mt-2 text-white/45">Create your first profit pool to start distributing.</p>
                                <button onClick={() => setCreatePoolOpen(true)} className="btn-neon mt-5 rounded-md px-4 py-2 text-sm" data-testid="empty-create-pool">Create pool</button>
                            </EmptyStateCard>
                        )}
                    </div>
                    <div className="dashboard-empty-banners shrink-0 space-y-4 sm:space-y-6">
                        <BetaModeNotice />
                        <OrgAlertsBanner />
                    </div>
                </div>
            )}

            {!showEmptyState && (
            <>
            <div className="dashboard-banners relative z-10 shrink-0 space-y-4 sm:space-y-6">
                <BetaModeNotice />
                <OrgAlertsBanner />
            </div>
            <div
                className="relative flex w-full flex-1 min-h-0 flex-col min-h-[28rem]"
                data-testid="dashboard-content-frame"
            >
                <div className={`flex flex-1 min-h-0 flex-col ${poolsLoading ? "pointer-events-none invisible" : ""}`} aria-hidden={poolsLoading}>
                {showDashboard && activePool && (
                    <div className="space-y-8" data-testid="dashboard-main-pane">
                        <div className="dashboard-toolbar flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4" data-testid="dashboard-toolbar">
                            <PoolSwitcher pools={pools} activeId={activePoolId} onSelect={selectPool} onCreate={() => setCreatePoolOpen(true)} />
                            <div className="dashboard-toolbar-actions flex min-w-0 max-w-full flex-nowrap items-center gap-2 lg:min-w-0 lg:flex-1 lg:flex-wrap lg:justify-end">
                                {can.editPercentages(role) && (
                                    <button type="button" onClick={() => setEditOpen(true)} className="dashboard-action-btn btn-ghost" data-testid="edit-percentages-button" data-testid-alt="edit-allocations-button">
                                        <Sliders className="h-3.5 w-3.5 shrink-0" aria-hidden /> Edit allocations
                                    </button>
                                )}
                                {can.invite(role) && (
                                    <button type="button" onClick={() => setInviteOpen(true)} className="dashboard-action-btn btn-ghost" data-testid="invite-recipient-button">
                                        <UserPlus className="h-3.5 w-3.5 shrink-0" aria-hidden /> Invite
                                    </button>
                                )}
                                {(can.executePayout(role) || can.deletePool(role)) && (
                                    <div className="dashboard-toolbar-payout-group inline-flex shrink-0 items-center gap-1.5">
                                        {can.executePayout(role) && (
                                            <button type="button" onClick={() => setExecuteOpen(true)} className="dashboard-action-btn btn-neon" data-testid="execute-payout-button" data-testid-alt="run-payout-button">
                                                <Send className="h-3.5 w-3.5 shrink-0" aria-hidden /> Run payout
                                            </button>
                                        )}
                                        {can.deletePool(role) && (
                                            <button
                                                type="button"
                                                onClick={() => setDeletePoolOpen(true)}
                                                className="dashboard-action-btn dashboard-action-btn--icon btn-ghost text-rose-300/90 hover:text-rose-300 hover:bg-rose-500/10"
                                                aria-label="Delete pool"
                                                title="Delete pool"
                                                data-testid="delete-pool-button"
                                            >
                                                <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <section className="grid min-h-[32rem] shrink-0 place-items-center py-8 pb-10 lg:hidden" data-testid="mobile-hero">
                            <div className="flex flex-col items-center text-center">
                                <div className="mb-4 flex items-center gap-2">
                                    <span className="status-dot" />
                                    <p className="label-eyebrow text-white/55">{activePool?.name} · Active</p>
                                </div>
                                <SplitWheel members={activePool.members || []} size={360} stroke={10} highlightId={highlightId} onHover={setHighlightId} />
                                <div className="mt-6"><SecurityBadge /></div>
                            </div>
                        </section>

                        <PoolOverview
                            pool={activePool}
                            sections="header"
                            className="hidden lg:block"
                            onCreateInvoice={can.createInvoice(role) ? () => setStripeOpen(true) : null}
                        />

                        <div className="hidden items-stretch gap-6 lg:grid lg:grid-cols-3">
                                <div className="relative col-span-2 flex min-h-[38rem] shrink-0 flex-col surface p-7 pb-8" data-testid="split-panel">
                                    <div className="pointer-events-none absolute inset-0" aria-hidden style={{ background: "radial-gradient(ellipse at center, rgba(0,191,13,0.07), transparent 70%)" }} />
                                    <div className="relative mb-4 flex shrink-0 items-center justify-between">
                                        <div>
                                            <p className="label-eyebrow">Allocations</p>
                                            <h3 className="mt-1 font-heading text-xl">The Split Wheel</h3>
                                        </div>
                                        <SecurityBadge />
                                    </div>
                                    <div className="relative grid w-full flex-1 place-items-center py-6 pb-8">
                                        <SplitWheel members={activePool.members || []} size={460} stroke={12} highlightId={highlightId} onHover={setHighlightId} />
                                    </div>
                                </div>
                                <div className="col-span-1 flex min-h-[38rem] min-w-0 flex-col self-stretch surface p-5" data-testid="recipients-panel">
                                    <div className="mb-4 flex shrink-0 items-end justify-between">
                                        <div>
                                            <p className="label-eyebrow">Recipients</p>
                                            <h3 className="mt-1 font-heading text-xl">Pool members</h3>
                                        </div>
                                        <span className="text-xs text-white/40">{wheelMembers(activePool.members || []).length} active</span>
                                    </div>
                                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                        <Recipients
                                        scrollable
                                        pool={activePool}
                                        members={activePool.members || []}
                                        highlightId={highlightId}
                                        onHover={setHighlightId}
                                        canManage={can.removeMember(role)}
                                        onSetBank={setBankFor}
                                        poolId={activePool.id}
                                        onMemberAvatarUpdated={(p) => updateActivePool(p)}
                                        onRemove={async (m) => {
                                            try {
                                                const { data } = await api.delete(`/pools/${activePool.id}/members/${m.id}`);
                                                if (data.status === "applied") {
                                                    toast.success(`Removed ${m.name}`);
                                                    refetch();
                                                } else {
                                                    toast.success("Removal queued for approval");
                                                    navigate("/approvals");
                                                }
                                            } catch (e) {
                                                toast.error(e.response?.data?.detail || e.message);
                                            }
                                        }}
                                    />
                                    </div>
                                </div>
                            </div>

                        <PoolOverview
                            pool={activePool}
                            sections="metrics"
                            className="hidden lg:block"
                        />

                        <PoolOverview
                            pool={activePool}
                            className="lg:hidden"
                            onCreateInvoice={can.createInvoice(role) ? () => setStripeOpen(true) : null}
                        />

                        <section className="surface flex flex-col gap-4 p-5 lg:hidden" data-testid="recipients-panel-mobile">
                                <div className="flex shrink-0 items-end justify-between">
                                    <h3 className="font-heading text-xl">Recipients</h3>
                                    <span className="text-xs text-white/40">{wheelMembers(activePool.members || []).length} active</span>
                                </div>
                                <Recipients
                                    scrollable
                                    className="max-h-[28rem]"
                                    pool={activePool}
                                    members={activePool.members || []}
                                    highlightId={highlightId}
                                    onHover={setHighlightId}
                                    canManage={can.removeMember(role)}
                                    onSetBank={setBankFor}
                                    poolId={activePool.id}
                                    onMemberAvatarUpdated={(p) => updateActivePool(p)}
                                    onRemove={async (m) => {
                                        try {
                                            const { data } = await api.delete(`/pools/${activePool.id}/members/${m.id}`);
                                            if (data.status === "applied") {
                                                toast.success(`Removed ${m.name}`);
                                                refetch();
                                            } else {
                                                toast.success("Removal queued for approval");
                                                navigate("/approvals");
                                            }
                                        } catch (e) {
                                            toast.error(e.response?.data?.detail || e.message);
                                        }
                                    }}
                                />
                            </section>

                        <OnboardingChecklist
                            pool={activePool}
                            memberships={memberships}
                            connectReady={connectReady}
                            onConnectStripe={() => navigate("/settings?tab=connections")}
                            onInvite={() => setInviteOpen(true)}
                            onLinkBank={(me) => setBankFor(me)}
                            onCreateInvoice={() => setStripeOpen(true)}
                        />

                        <div className="grid gap-6 lg:grid-cols-2">
                            <ProfitSimulator pool={activePool} />
                            <div className="space-y-6">
                                <ClientPayments payments={clientPayments} currency={activePool.currency || "GBP"} />
                                <PayoutHistory payouts={payouts} poolId={activePool.id} poolName={activePool?.name} currency={activePool.currency || "GBP"} />
                            </div>
                        </div>

                        <EditPercentagesDialog pool={activePool} open={editOpen} onClose={() => setEditOpen(false)} onChanged={onChanged} />
                        <BankAccountDialog pool={activePool} member={bankFor} open={!!bankFor} onClose={() => setBankFor(null)} onSaved={(p) => updateActivePool(p)} />
                        <InviteRecipientDialog pool={activePool} orgId={activeOrg?.id} open={inviteOpen} onClose={() => setInviteOpen(false)} onInvited={() => refetch()} />
                        <ExecutePayoutDialog pool={activePool} open={executeOpen} onClose={() => setExecuteOpen(false)} onExecuted={onChanged} />
                        <StripeInvoiceDialog pool={activePool} open={stripeOpen} onClose={() => setStripeOpen(false)} />
                        <PoolDeleteConfirmModal
                            open={deletePoolOpen}
                            onClose={() => setDeletePoolOpen(false)}
                            onConfirm={handleDeletePool}
                            poolName={activePool?.name || ""}
                            hasHistory={poolHasHistory}
                            busy={deletePoolBusy}
                        />
                    </div>
                )}
                </div>
                {poolsLoading && (
                    <div className="absolute inset-0 z-10 grid place-items-center" data-testid="dashboard-loading">
                        <LoadingIndicator label="Loading pools…" />
                    </div>
                )}
            </div>
            </>
            )}

            </div>

            <CreatePoolDialog
                open={createPoolOpen}
                onClose={() => setCreatePoolOpen(false)}
                onCreated={(p) => {
                    setCreatePoolOpen(false);
                    if (p?.id) {
                        refetch().then(() => selectPool(p.id));
                    } else {
                        refetch();
                    }
                }}
            />
        </PageShell>
    );
}
